import { Link, useLocation, matchPath } from 'react-router-dom';

// material-ui
import Box from '@mui/material/Box';
import useMediaQuery from '@mui/material/useMediaQuery';
import Avatar from 'components/@extended/Avatar';
import Chip from '@mui/material/Chip';
import Badge from '@mui/material/Badge';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';

// project imports
import Dot from 'components/@extended/Dot';
import IconButton from 'components/@extended/IconButton';
import CrownIcon from 'components/icons/CrownIcon';
import { SidebarMenuButton, SidebarMenuItem } from '../../SidebarComponents';

// third-party
import { FormattedMessage } from 'react-intl';

import { MenuOrientation, NavActionType } from 'config';
import useConfig from 'hooks/useConfig';
import useAuth from 'hooks/useAuth';
import { handlerDrawerOpen, useGetMenuMaster } from 'api/menu';
import { formatWaUnreadBadge, useWhatsAppUnread } from 'contexts/WhatsAppUnreadContext';

// types
import { LinkTarget, NavItemType } from 'types/menu';

interface Props {
  item: NavItemType;
  level: number;
  isParents?: boolean;
  setSelectedID?: React.Dispatch<React.SetStateAction<string | undefined>>;
}

// ==============================|| NAVIGATION - LIST ITEM ||============================== //

export default function NavItem({ item, level, isParents = false, setSelectedID }: Props) {
  const { user } = useAuth();
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;

  // Contador de não-lidas do WhatsApp (só aplica ao item de menu do WhatsApp).
  const { total: waUnread } = useWhatsAppUnread();
  const showWaBadge = item.id === 'whatsapp' && waUnread > 0;
  const waUnreadLabel = formatWaUnreadBadge(waUnread);

  const downLG = useMediaQuery((theme) => theme.breakpoints.down('lg'));

  const hasRule = !item.rule || (user?.rules ?? []).includes(item.rule);
  const showPremiumIcon = !!item.permissionDisabled && !!item.rule && !hasRule;
  const PremiumIconComponent = (item.premiumIcon ?? CrownIcon) as React.ComponentType<{ sx?: any }>;

  const { menuOrientation } = useConfig();
  let itemTarget: LinkTarget = '_self';
  if (item.target) {
    itemTarget = '_blank';
  }

  const itemHandler = () => {
    if (downLG) handlerDrawerOpen(false);

    if (isParents && setSelectedID) {
      setSelectedID(item.id);
    }
  };

  const Icon = item.icon!;
  const baseIcon = item.icon ? (
    <Icon
      style={{
        fontSize: drawerOpen ? '1rem' : '1.25rem',
        ...(menuOrientation === MenuOrientation.HORIZONTAL && isParents && { fontSize: 20, stroke: '1.5' })
      }}
    />
  ) : (
    false
  );
  // Sobrepõe o contador no próprio ícone (vale com a sidebar aberta e recolhida).
  const itemIcon =
    showWaBadge && baseIcon ? (
      <Badge
        color="error"
        badgeContent={waUnreadLabel}
        overlap="circular"
        sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', height: 16, minWidth: 16, px: 0.5 } }}
      >
        {baseIcon}
      </Badge>
    ) : (
      baseIcon
    );

  const { pathname } = useLocation();
  const isSelected = !!matchPath({ path: item?.link ? item.link : item.url!, end: false }, pathname);

  return (
    <>
      {menuOrientation === MenuOrientation.VERTICAL || downLG ? (
        <SidebarMenuItem>
          <SidebarMenuButton
            component={Link}
            to={item.url!}
            target={itemTarget}
            icon={itemIcon}
            selected={isSelected}
            disabled={item.disabled}
            onClick={() => itemHandler()}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <FormattedMessage id={item.title as string} />
              <Box component="span" sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {showPremiumIcon && <PremiumIconComponent sx={{ fontSize: '1rem', transform: 'rotate(35deg)', color: 'warning.main' }} />}
                {item.chip && (
                  <Chip
                    color={item.chip.color}
                    variant={item.chip.variant}
                    size={item.chip.size}
                    label={item.chip.label}
                    avatar={
                      item.chip.avatar && (
                        <Avatar size="xs" color="primary">
                          {item.chip.avatar}
                        </Avatar>
                      )
                    }
                  />
                )}
              </Box>
            </Box>
          </SidebarMenuButton>
          {(drawerOpen || (!drawerOpen && level !== 1)) &&
            item?.actions &&
            item?.actions.map((action, index) => {
              const ActionIcon = action.icon!;
              const callAction = action?.function;
              return (
                <IconButton
                  key={index}
                  {...(action.type === NavActionType.FUNCTION && {
                    onClick: (event) => {
                      event.stopPropagation();
                      callAction();
                    }
                  })}
                  {...(action.type === NavActionType.LINK && {
                    component: Link,
                    to: action.url,
                    target: action.target ? '_blank' : '_self'
                  })}
                  color="secondary"
                  variant="outlined"
                  sx={{
                    position: 'absolute',
                    top: 12,
                    right: 20,
                    zIndex: 1202,
                    width: 20,
                    height: 20,
                    mr: -1,
                    ml: 1,
                    color: 'secondary.dark',
                    borderColor: isSelected ? 'primary.light' : 'secondary.light',
                    '&:hover': { borderColor: isSelected ? 'primary.main' : 'secondary.main' }
                  }}
                >
                  <ActionIcon style={{ fontSize: '0.625rem' }} />
                </IconButton>
              );
            })}
        </SidebarMenuItem>
      ) : (
        <ListItemButton
          component={Link}
          to={item.url!}
          target={itemTarget}
          disabled={item.disabled}
          selected={isSelected}
          onClick={() => itemHandler()}
          sx={{
            zIndex: 1201,
            ...(isParents && { p: 1, mr: 1 })
          }}
        >
          {itemIcon && (
            <ListItemIcon
              sx={{
                minWidth: 28,
                ...(!drawerOpen && {
                  borderRadius: 1.5,
                  width: 28,
                  height: 28,
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  '&:hover': { bgcolor: 'transparent' }
                }),
                ...(!drawerOpen && isSelected && { bgcolor: 'transparent', '&:hover': { bgcolor: 'transparent' } })
              }}
            >
              {itemIcon}
            </ListItemIcon>
          )}

          {!itemIcon && (
            <ListItemIcon
              sx={{
                color: isSelected ? 'primary.main' : 'secondary.dark',
                ...(!drawerOpen && {
                  borderRadius: 1.5,
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  '&:hover': { bgcolor: 'transparent' }
                }),
                ...(!drawerOpen && isSelected && { bgcolor: 'transparent', '&:hover': { bgcolor: 'transparent' } })
              }}
            >
              <Dot size={4} color={isSelected ? 'primary' : 'secondary'} />
            </ListItemIcon>
          )}
          <ListItemText
            primary={
              <Typography variant="h6" color={isSelected ? 'primary.main' : 'secondary.dark'}>
                <FormattedMessage id={item.title as string} />
              </Typography>
            }
          />
          {(drawerOpen || (!drawerOpen && level !== 1)) && item.chip && (
            <Chip
              color={item.chip.color}
              variant={item.chip.variant}
              size={item.chip.size}
              label={item.chip.label}
              avatar={
                item.chip.avatar && (
                  <Avatar size="xs" color="primary">
                    {item.chip.avatar}
                  </Avatar>
                )
              }
            />
          )}
        </ListItemButton>
      )}
    </>
  );
}
